import { Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import { RoutalLoader, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { debounce } from 'lodash';
import { useState } from 'react';

import { createPortal } from 'react-dom';
import { DRIVERS_LAYOUT_ID } from '../../../../../../components/DriversLayout';
import { Modal } from '../../../../../../components/Modal';
import { SearchInput } from '../../../../../../components/searchInput/SearchInput';
import { SlidingMenuWithBack } from '../../../../../../components/SlidingMenuWithBack';
import { intl } from '../../../../../../helpers/IntGlobalProvider';
import Button from '../../../../../../react-components/Button';
import { Text } from '../../../../../../react-components/Text';
import { AddStopsList } from '../../AddStopsList';
import { BUTTON_HEIGHT, ButtonContainer, ValidatedAddress } from './ValidateStopsBulk';

interface EditStopAddressProps {
  initialValidatedAddress?: ValidatedAddress;
  isLoading?: boolean;
  isOpen: boolean;
  onUpdate: (newValidatedAddress: ValidatedAddress) => void;
  onDelete?: () => void;
  onClose: () => void;
  portalElementId?: string;
}

export const EditStopAddress = ({
  initialValidatedAddress,
  isLoading,
  isOpen,
  onDelete,
  onUpdate,
  onClose,
  portalElementId = DRIVERS_LAYOUT_ID,
}: EditStopAddressProps) => {
  const [newRawAddress, setNewRawAddress] = useState<string>(initialValidatedAddress?.rawAddress ?? ``);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);

  const updateRawAddress = debounce((newValue: string) => {
    setNewRawAddress(newValue);
  }, 250);

  const content = (
    <>
      <SlidingMenuWithBack
        id="edit-stop-address"
        direction="up"
        background={RoutalPalette.neutral02n}
        isOpen={isOpen}
        onClose={onClose}
        slideStyles={{ inset: 0 }}
        headerContent={
          <Grid item container direction="row" wrap="nowrap" alignItems="center" height="70px">
            <Text variant="h6" isBold>
              {initialValidatedAddress?.rawAddress}
            </Text>
          </Grid>
        }
        childrenContainerStyles={{ padding: 0, flexWrap: `nowrap`, overflowY: `auto` }}
        footerContent={
          onDelete ? (
            <ButtonContainer>
              <Button
                onClick={() => {
                  setConfirmDelete(true);
                }}
                disabled={isLoading}
                variant="flat"
                styleType="danger"
                intlId="driver.stops.create_stops.add_bulk.validate.delete_stop.title"
                style={{
                  height: `${BUTTON_HEIGHT}px`,
                  maxHeight: `${BUTTON_HEIGHT}px`,
                  padding: 0,
                  margin: 0,
                }}
              />
            </ButtonContainer>
          ) : null
        }
      >
        <SearchInput
          initialValue={newRawAddress}
          inputContainerStyles={{
            border: `1px solid ${RoutalPalette.neutral10}`,
            backgroundColor: RoutalPalette.neutral02n,
            borderRadius: `50px`,
            padding: `15px`,
            margin: `20px`,
            width: `auto`,
          }}
          source={`Stop search`}
          disabled={isLoading}
          voiceInput
          searchLive
          autoFocus
          onTextChange={(value) => updateRawAddress(value)}
          placeholder={intl.formatMessage({ id: `driver.stops.edit_stops` })}
        />
        <Grid container flexDirection="column" maxHeight="100%" overflow="auto" padding="20px">
          {!isLoading ? (
            <AddStopsList
              searchText={newRawAddress}
              icon={`add`}
              onSelectLocation={(location: Location) => {
                onUpdate({
                  rawAddress: newRawAddress ? newRawAddress : initialValidatedAddress?.rawAddress ?? location.label!,
                  location,
                  status: `successful`,
                });
              }}
              refreshSession={false}
            />
          ) : (
            <RoutalLoader />
          )}
        </Grid>
      </SlidingMenuWithBack>

      <Modal
        open={confirmDelete}
        title={{
          intlId: `driver.stops.create_stops.add_bulk.validate.delete_stop.title`,
        }}
        subtitle={{
          intlId: `driver.stops.create_stops.add_bulk.validate.delete_stop.info`,
        }}
        buttons={[
          {
            intlId: `driver.stops.create_stops.add_bulk.validate.delete_stop.confirm`,
            onClick: () => {
              onDelete && onDelete();
            },
            style: {
              backgroundColor: RoutalPalette.error.medium,
              borderColor: RoutalPalette.error.medium,
              color: RoutalPalette.white,
              border: 0,
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setConfirmDelete(false);
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />
    </>
  );
  const element = document.getElementById(portalElementId ?? ``);
  if (element) {
    return createPortal(content, element as HTMLElement);
  }

  return content;
};
